import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, FormControl, Button,
  RadioGroup, FormControlLabel, Radio, ToggleButtonGroup,
  ToggleButton, Typography, Select, MenuItem, InputLabel
} from '@mui/material';
import * as xlsx from 'xlsx';
import dayjs from 'dayjs';
import AskDialog from '../../components/common/dialog/AskDialog';
import ProfitresultsStaticsHqContainer from './ProfitresultsStaticsHqContainer';
import ProfitresultsStaticsTeamContainer from './ProfitresultsStaticsTeamContainer';
import * as salesCostsApi from '../../lib/api/salesCosts';

const ProfitresultsStaticsContainer = () => {
  const hqTableRef = useRef();
  const dpTableRef = useRef();

  // [수정] 2025년부터 (현재 연도 + 1년)까지 목록 생성
  const startYear = 2025;
  const nextYear = dayjs().add(1, 'year').year(); // 현재 날짜 기준 다음 해 계산
  const years = Array.from(
    { length: nextYear - startYear + 1 }, 
    (_, i) => startYear + i
  );

  const [searchYear, setSearchYear] = useState(dayjs().year()); // 기본값은 현재 연도
  const [startMonth, setStartMonth] = useState('01');
  const [endMonth, setEndMonth] = useState(dayjs().format('MM'));

  const [searchPrjTyp, setSearchPrjTyp] = useState('A');
  const [type, setType] = useState('headquarter');
  const [searchData, setSearchData] = useState(null);
  const [visible, setVisible] = useState(false);
  const [isReady, setIsReady] = useState(false); 
  const [maxAvailableMonth, setMaxAvailableMonth] = useState('');

  const months = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));

  const updateSearchData = useCallback(() => {
    const currentEndMonth = endMonth || dayjs().format('MM');
    const startStr = `${searchYear}-${startMonth}-01`;
    const endStr = dayjs(`${searchYear}-${currentEndMonth}-01`).endOf('month').format('YYYY-MM-DD');
    setSearchData(`${type}_${searchPrjTyp}_${startStr}~${endStr}`);
  }, [type, searchPrjTyp, searchYear, startMonth, endMonth]);

  const searchClickHandler = () => {
    if (Number(startMonth) > Number(endMonth)) {
      setVisible(true);
      return;
    }
    updateSearchData();
  };

  // API 호출 및 종료월 자동 설정
  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await salesCostsApi.getSalesCostsStaticTotalViewNew(String(searchYear));
        const result = response.data.list;

        if (result && result.length > 0) {
          const sortedResult = [...result].sort((a, b) => Number(b.dtMonth) - Number(a.dtMonth));
          const latestMonthStr = String(sortedResult[0].dtMonth).padStart(2, '0');

          setMaxAvailableMonth(Number(latestMonthStr));
          setEndMonth(latestMonthStr);
          setIsReady(true); 
        } else {
          setEndMonth(dayjs().format('MM'));
          setIsReady(true);
        }
      } catch (error) {
        console.error("데이터 로드 실패:", error);
        setIsReady(true);
      }
    };
    fetchData();
  }, [searchYear]);

  useEffect(() => {
    if (!isReady) return; 
    if (Number(startMonth) > Number(endMonth)) return;
    updateSearchData();
  }, [isReady, searchYear, startMonth, endMonth, searchPrjTyp, type, updateSearchData]);

  const handleDownExcel = () => {
    const refs = { headquarter: hqTableRef, depart: dpTableRef };
    const titles = { headquarter: '본부', depart: '팀' };
    const targetRef = refs[type]?.current;
    if (targetRef) {
      const wb = xlsx.utils.table_to_book(targetRef, { sheet: 'sheet1', raw: true });
      xlsx.writeFile(wb, `${titles[type]}_매출통계_${dayjs().format('YYYYMMDD')}.xlsx`);
    }
  };

  const getSubContainerProps = (ref) => ({
    type,
    selectedYear: String(searchYear),
    strMonth: startMonth,
    strDay: '01',
    endMonth: endMonth,
    endDay: dayjs(`${searchYear}-${endMonth}-01`).endOf('month').format('DD'),
    tableRef: ref,
    searchData,
    selectPrjTyp: searchPrjTyp,
  });

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, flexWrap: 'wrap', gap: 1.5, my: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>조회 연도</InputLabel>
            <Select value={searchYear} label="조회 연도" onChange={(e) => setSearchYear(e.target.value)}>
              {years.map(y => <MenuItem key={y} value={y}>{y}년</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>시작월</InputLabel>
            <Select value={startMonth} label="시작월" onChange={(e) => setStartMonth(e.target.value)}>
              {months.map(m => <MenuItem key={m} value={m}>{Number(m)}월</MenuItem>)}
            </Select>
          </FormControl>
          <Typography sx={{ mx: 0.5 }}>~</Typography>
          <FormControl size="small" sx={{ minWidth: 90 }}>
            <InputLabel>종료월</InputLabel>
            <Select value={endMonth} label="종료월" onChange={(e) => setEndMonth(e.target.value)}>
              {months.map(m => <MenuItem key={m} value={m}>{Number(m)}월</MenuItem>)}
            </Select>
          </FormControl>
        </Box>

        <FormControl>
          <RadioGroup row value={searchPrjTyp} onChange={(e) => setSearchPrjTyp(e.target.value)}>
            <FormControlLabel value="A" control={<Radio size="small" />} label="전체" />
          </RadioGroup>
        </FormControl>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup size="small" color="primary" value={type} exclusive onChange={(_, val) => val && setType(val)}>
            <ToggleButton value="headquarter">본부</ToggleButton>
            <ToggleButton value="depart">팀</ToggleButton>
          </ToggleButtonGroup>
          {maxAvailableMonth && (
            <Typography variant="body2" sx={{ color: '#d93025', fontWeight: 700, fontSize: '0.85rem', ml: 1 }}>
              {`※ 판관비가 ${maxAvailableMonth}월까지 등록되어 있습니다.`}
            </Typography>
          )}
        </Box>

        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" color="info" onClick={searchClickHandler}>조회</Button>
          <Button variant="contained" color="success" onClick={handleDownExcel}>Excel</Button>
        </Box>
      </Box>

      <Box sx={{ mt: 2 }}>
        {searchData && type === 'headquarter' && <ProfitresultsStaticsHqContainer {...getSubContainerProps(hqTableRef)} />}
        {searchData && type === 'depart' && <ProfitresultsStaticsTeamContainer {...getSubContainerProps(dpTableRef)} />}
      </Box>

      <AskDialog
        visible={visible}
        title="조회 기간 오류"
        description="시작월이 종료월보다 늦을 수 없습니다."
        onConfirm={() => setVisible(false)}
        onCancle={() => setVisible(false)}
      />
    </Box>
  );
};

export default ProfitresultsStaticsContainer;
